<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col border-l border-gray-200 bg-gray-50">
    <div class="bg-white px-5 pt-5">
      <h3 class="m-0 text-base font-semibold text-gray-900">Split across weeks — {{ title }}</h3>
      <p class="mb-0 mt-1 text-xs text-gray-500">{{ split.problem.value }}</p>

      <span class="mb-2 mt-4 block text-[11px] font-bold uppercase tracking-wider text-gray-500">1 · How should it be placed?</span>
      <div class="flex gap-3 pb-4" role="radiogroup" aria-label="How should it be placed">
        <SplitStrategyCard
          title="Fill weeks to capacity"
          description="Each week takes what it has left, starting with the first week. The rest moves on to the next planned week."
          tooltip="Fills each week to what it has left, starting with the first week, and carries the rest to the next planned week. Never overbooks."
          :consequence="split.fillLine.value"
          :selected="split.preset.value === 'fill'"
          :disabled="split.locked.value"
          @select="split.applyPreset('fill')"
        >
          <template #icon>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <rect x="1" y="2" width="3" height="12" rx=".5" />
              <rect x="5.5" y="5" width="3" height="9" rx=".5" />
              <rect x="10" y="9" width="3" height="5" rx=".5" />
            </svg>
          </template>
        </SplitStrategyCard>
        <SplitStrategyCard
          title="Spread evenly"
          description="The same amount in every week, whether it fits or not."
          tooltip="Divides the PO equally over the next N planned weeks, ignoring capacity. Weeks that cannot take their share turn red."
          :consequence="split.evenLine.value"
          :selected="split.preset.value === 'even'"
          :disabled="split.locked.value"
          @select="split.applyPreset('even')"
        >
          <template #icon>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <rect x="1" y="5" width="3" height="9" rx=".5" />
              <rect x="5.5" y="5" width="3" height="9" rx=".5" />
              <rect x="10" y="5" width="3" height="9" rx=".5" />
            </svg>
          </template>
          <template #title-extra>
            <span class="inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-700">
              over
              <span class="inline-flex overflow-hidden rounded-md border border-gray-300 bg-white">
                <button
                  class="px-2 text-gray-500"
                  type="button"
                  aria-label="Fewer weeks"
                  :disabled="split.locked.value || split.evenN.value <= 1"
                  @click.stop="split.setEvenN(split.evenN.value - 1)"
                >
                  −
                </button>
                <b class="border-x border-gray-300 px-2 tabular-nums">{{ split.evenN.value }}</b>
                <button
                  class="px-2 text-gray-500"
                  type="button"
                  aria-label="More weeks"
                  :disabled="split.locked.value || split.evenN.value >= split.maxEven.value"
                  @click.stop="split.setEvenN(split.evenN.value + 1)"
                >
                  +
                </button>
              </span>
              {{ split.evenN.value === 1 ? 'week' : 'weeks' }}
            </span>
          </template>
        </SplitStrategyCard>
      </div>
    </div>

    <div class="flex items-baseline justify-between border-t border-gray-200 px-5 pt-3">
      <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500">2 · The plan · change any number</span>
      <span class="text-[11px] text-gray-400">
        One bar per type:
        <b class="text-gray-500">grey</b>
        is already booked,
        <b class="text-blue-700">blue</b>
        is this order, light is what is left.
      </span>
    </div>

    <!-- The only scroll container in the pane; header, cards and footer stay put. -->
    <div class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-scroll px-5 pb-3 pt-2 [scrollbar-gutter:stable]">
      <SplitWeekCard
        v-for="(card, index) in split.cards.value"
        :key="card.week.weekKey"
        :card="card"
        :swatch="SWATCHES[index % SWATCHES.length]"
        :disabled="split.locked.value"
        :can-add-plan="canAddPlan"
        :menu="noPlanMenu"
        @change="(typeId, value) => split.setCell(card.week.weekKey, typeId, value)"
        @retry="split.retry()"
        @show-on-board="(weekKey) => $emit('showOnBoard', weekKey)"
      />
      <button class="self-start px-4 py-1 text-xs font-medium text-blue-700 hover:underline" type="button" :disabled="split.locked.value" @click="split.showMoreWeeks()">
        Show later weeks
      </button>
    </div>

    <div class="flex items-center justify-between gap-3 border-t px-5 py-3 text-[13px] text-gray-700" :class="footerClass">
      <span v-if="footer.kind === 'saving'" class="inline-flex items-center gap-2">
        <span class="spinner text-blue-700" aria-hidden="true" />
        <span>
          <b>Saving week {{ footer.index + 1 }} of {{ footer.total }}…</b>
          Each week becomes its own block on the board. If one fails, the others stay saved and you can retry it.
        </span>
      </span>
      <span v-else-if="footer.kind === 'halted'" class="inline-flex items-center gap-2">
        <span class="badge bg-red-600">!</span>
        <span>
          <b>{{ footer.label }} did not save</b>
          · {{ haltedDetail }}
        </span>
      </span>
      <span v-else-if="footer.kind === 'over'" class="inline-flex items-center gap-2">
        <span class="badge bg-red-600">!</span>
        <span>
          <b>{{ pluralWeeks(footer.weeks.length) }} {{ footer.weeks.length === 1 ? 'is' : 'are' }} over capacity</b>
          · {{ footer.weeks.map((w) => `${w.label} by ${w.parts.join(' and ')}`).join(', ') }}. Scheduling will ask you to confirm.
        </span>
      </span>
      <template v-else-if="footer.kind === 'open'">
        <span class="inline-flex items-center gap-2">
          <span class="badge bg-amber-600">!</span>
          <span>
            <b>{{ footer.parts.join(' and ') }} not placed yet.</b>
            Type it into a week, or:
          </span>
        </span>
        <span class="inline-flex gap-3 whitespace-nowrap">
          <button class="text-xs font-medium text-blue-700 hover:underline" type="button" @click="split.fillOpen()">Fill weeks to capacity</button>
          <button class="text-xs font-medium text-blue-700 hover:underline" type="button" @click="split.addWeek()">+ Add week</button>
        </span>
      </template>
      <span v-else class="inline-flex items-center gap-2">
        <span class="badge bg-green-600">✓</span>
        <span>
          <b>Everything is placed.</b>
          {{ footer.parts.join(' and ') }} across {{ pluralWeeks(footer.weeks) }}, nothing over capacity.
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue';

  import SplitStrategyCard from '@/components/scheduling/SplitStrategyCard.vue';
  import SplitWeekCard from '@/components/scheduling/SplitWeekCard.vue';
  import { useAccess } from '@/composables/useAccess';
  import { useNoPlanMenu } from '@/composables/useNoPlanMenu';
  import type { useSplitAcrossWeeks } from '@/composables/useSplitAcrossWeeks';
  import { PERMISSIONS } from '@/types/access';
  import { pluralWeeks } from '@/utils/splitAcrossWeeks';

  const props = defineProps<{ split: ReturnType<typeof useSplitAcrossWeeks>; title: string }>();
  defineEmits<{ showOnBoard: [weekKey: string] }>();

  const { hasPermission } = useAccess();
  // One instance for the whole pane: at most one week's create-plan menu is ever open.
  const noPlanMenu = useNoPlanMenu();
  // Planning a week is a capacity edit, not a PO edit — same permission the board's no-plan
  // cell checks. Without it a skipped week's add-capacity lines stay inactive.
  const canAddPlan = computed(() => hasPermission(PERMISSIONS.WRITE_CAPACITY));

  // A save in flight disables the buttons; a menu left open behind one must not reappear
  // when the split unlocks after a retry or a reset.
  watch(
    () => props.split.locked.value,
    (locked) => {
      if (locked) noPlanMenu.closeAny();
    }
  );

  // Cosmetic per-week swatch, a fixed blue ramp (spec).
  const SWATCHES = ['#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'];

  const footer = computed(() => props.split.footer.value);
  // filter(Boolean) so an empty `parts` (nothing was placed in the failed week) doesn't leave
  // a doubled "·" between "did not save" and "Retry it from the plan".
  const haltedDetail = computed(() => {
    const state = footer.value;
    if (state.kind !== 'halted') return '';
    return [state.parts.join(' and '), 'Retry it from the plan'].filter(Boolean).join(' · ');
  });
  const footerClass = computed(
    () =>
      ({
        saving: 'border-blue-200 bg-blue-50',
        halted: 'border-red-200 bg-red-50',
        over: 'border-red-200 bg-red-50',
        open: 'border-amber-300 bg-amber-50',
        complete: 'border-gray-200 bg-green-50',
      })[footer.value.kind]
  );
</script>

<style scoped>
  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    flex-shrink: 0;
  }
  .spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .spinner {
      animation: none;
    }
  }
</style>
